<route lang="yaml">
meta:
  layout: escuela
</route>

<script setup lang="ts">
import type { BuilderContent } from '@builder.io/sdk-vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { Content, fetchOneEntry, isPreviewing } from '@builder.io/sdk-vue';
import { computed, onMounted, ref, watch, onBeforeMount } from 'vue';
import { registeredComponents } from '@/init-builder.js';
import { useAuthStore } from "@/stores/auth";
import { useTalleresStore } from '@/stores/talleres';
import { miUsuario } from '@/services/usuarios';
import { BUILDER_API_KEY } from '@/config/builder';

const stats = ref({});
const statsAudio = ref({});
const talleresStore = useTalleresStore();
const content = ref<BuilderContent | null>(null);
const apiKey = BUILDER_API_KEY;
const canShowContent = ref(false);
const model = 'page';
const route = useRoute();

const socio = ref({/* ... */ }); // Tu objeto socio aquí

const estadisticas = ref({});
const isLoading = ref(true);
const isContentReady = ref(false);

const store = useAuthStore();
const usuario = computed(() => store.usuario);
let idCuento = 1835;
const SERVER = 'https://staging-strapi.orsai.org';

// Definir el titulo de la pagina como Cargando...
document.title = 'Cargando...';

// Función para cargar el contenido
const loadContent = async () => {
    const idContent = route.params.id || window.location.pathname.split('/').pop();

    // Iniciamos todas las promesas en paralelo
    const contentPromise = fetchOneEntry({
        model,
        apiKey,
        userAttributes: {
            urlPath: `/escuela/${idContent}`,
        },
        cacheSeconds: -1,
    });


    const estadisticasPromise = window.location.pathname.includes('estadisticas')
        ? loadEstadisticas()
        : Promise.resolve();

    // Esperamos a que todas las promesas se resuelvan
    const [contentValue] = await Promise.all([contentPromise, estadisticasPromise]);

    console.log('contentValue', contentValue);

    // Cuando lo obtiene, asignar el titulo de la pagina
    document.title = contentValue?.data?.title || 'Cargando...';

    content.value = contentValue;

    if (usuario.value) {
        socio.value = usuario.value;
    }

    canShowContent.value = content.value ? true : isPreviewing();
    isContentReady.value = true;

    // Pequeña pausa antes de ocultar el loader
    setTimeout(() => {
        isLoading.value = false;
    }, 300);
};

// Función separada para cargar estadísticas
const loadEstadisticas = async () => {
    let idUsuario = usuario.value?.id;
    // if (!idUsuario) {
    //     const usuario = await miUsuario();
    //     idUsuario = usuario?.id;
    // }

    if ([1, 1384, 3].includes(idUsuario) || isPreviewing()) {
        idUsuario = 100801;
    }

    const responseCuento = await axios.get(`${SERVER}/api/cuentos`, {
        params: {
            limit: 1,
            fields: ['id', 'idUsuario', 'taller'],
            filters: {
                idUsuario: idUsuario,
                taller: {
                    $eq: 'dos'
                }
            },
        },
    });

    idCuento = responseCuento.data.data[0]?.id;
    talleresStore.setIdCuento(idCuento);

    // Cargamos estadísticas en paralelo
    await Promise.all([
        talleresStore.fetchEstadisticas(),
    ]);

    stats.value = talleresStore.estadisticas;
    statsAudio.value = talleresStore.estadisticasAudio;
};

// Iniciamos la carga antes de que el componente se monte
onBeforeMount(() => {
    loadContent();
});

const path = computed(() => route.params?.id);

watch(path, () => {
    loadContent();
});

const getRegisteredComponents = () => registeredComponents;
</script>

<template>
    <div class="max-w-6xl mx-auto mb-10 lg:mb-32 md:p-5 p-2 relative">
        <transition name="fade">
            <Content v-if="canShowContent && isContentReady" :model="model" :content="content" :api-key="apiKey"
                :data="{ socio, stats, statsAudio }" :customComponents="getRegisteredComponents()" />
        </transition>
    </div>
</template>

<style lang="scss">
.fade-enter-active,
.fade-leave-active {
    transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}

.content-ready {
    transition: opacity 0.3s ease-in;
}

.content-ready-enter,
.content-ready-leave-to {
    opacity: 0;
}

.content-ready-enter-to,
.content-ready-leave {
    opacity: 1;
}

h1 {
    @apply text-2xl font-bold my-2;
}

h2 {
    @apply text-xl font-semibold;
}

h3 {
    @apply text-lg font-semibold text-gray-600;
}

p {
    @apply text-sm leading-relaxed;
}

.con-borde-inferior {
    @apply mb-2 border-b border-gray-200 pb-3;
}

.tarjeta-datos {
    @apply p-4 bg-white border border-gray-200 rounded-lg shadow-sm text-sm my-4;
}

.item-estadistica {
    @apply flex justify-between items-center mb-4 border-b border-gray-200 pb-4;
}

.valor-estadistica {
    @apply text-gray-600 font-semibold md:text-lg flex items-center gap-2;
}

.copete {
    @apply text-gray-600 text-[1.125rem] px-2 py-2;
}

.badge-count {
    @apply w-12 h-6 flex items-center justify-center bg-blue-500 text-white font-medium text-xs rounded-full;
}

.last-no-border:last-child {
    @apply border-b-0 pb-0;
}

.control-grafico {
    @apply flex items-center justify-end my-4 lg:hidden;
}

.toggle-button {
    @apply relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500;
}

.toggle-indicator {
    @apply inline-block w-4 h-4 transform bg-white rounded-full transition-transform;
}

/* En desktop max-w-[75%] */
@media (min-width: 1024px) {
    .copete {
        @apply max-w-[75%];
    }
}
</style>